import dynamic from 'next/dynamic';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import logCustomRouteButtonClicked from '@alltrails/amplitude/events/logCustomRouteButtonClicked';
import useUser from '@alltrails/context/hooks/useUser';
import { useClientNavigationPushState, useClientNavigationSearchParams } from '@alltrails/core/hooks/useClientNavigation';
import { useLanguageRegionCode } from '@alltrails/core/hooks/useLanguageRegionCode';
import { wrapUrlSafe } from '@alltrails/core/utils/wrapUrlSafe';
import Button from '@alltrails/denali/components/Button';
import IconButton from '@alltrails/denali/components/IconButton';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';
import DrawPath from '@alltrails/denali/icons/DrawPath';
import styles from './BuildCustomRouteButton.module.scss';

const SignUpModal = dynamic(() => import('@alltrails/user-auth/components/SignUpModal'), { ssr: false });

const BuildCustomRouteButton = () => {
  const intl = useIntl();
  const user = useUser();
  const pushState = useClientNavigationPushState();
  const searchParams = useClientNavigationSearchParams();
  const languageRegionCode = useLanguageRegionCode();
  const isMobile = useIsMobileSizedScreen();
  const [showIconButton, setShowIconButton] = useState(false);

  const buttonText = intl.formatMessage({ defaultMessage: 'Build custom route' });
  const [isSignUpModalOpen, setIsSignUpModalOpen] = useState(false);
  const didLoginRef = useRef(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowIconButton(true);
    }, 5000);

    return () => clearTimeout(timer);
  }, []);

  const returnTo = useMemo(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }
    const currentPath = window.location.pathname + window.location.search;
    return encodeURIComponent(currentPath);
  }, []);

  const onButtonClick = useCallback(() => {
    logCustomRouteButtonClicked({ location: 'explore map' });

    if (user) {
      pushState(wrapUrlSafe(`/explore/custom-routes/new?${searchParams.toString()}${returnTo ? `&returnTo=${returnTo}` : ''}`, languageRegionCode));
    } else {
      setIsSignUpModalOpen(true);
      return;
    }
  }, [user, pushState, searchParams, languageRegionCode, returnTo]);

  useEffect(() => {
    if (user && didLoginRef.current && !isSignUpModalOpen) {
      pushState(wrapUrlSafe(`/explore/custom-routes/new?${searchParams.toString()}${returnTo ? `&returnTo=${returnTo}` : ''}`, languageRegionCode));
    }
  }, [isSignUpModalOpen, languageRegionCode, pushState, searchParams, user, returnTo]);

  return (
    <>
      {showIconButton || isMobile ? (
        <IconButton
          icon={{ Component: DrawPath }}
          onClick={onButtonClick}
          title={buttonText}
          variant="elevated"
        />
      ) : (
        <Button
          className={styles.buildCustomRouteButton}
          icon={{ Component: DrawPath }}
          onClick={onButtonClick}
          title={buttonText}
          text={buttonText}
          variant="elevated"
        />
      )}
      {isSignUpModalOpen && (
        <SignUpModal
          isOpen
          closeModal={() => setIsSignUpModalOpen(false)}
          onSuccess={() => {
            setIsSignUpModalOpen(false);
            didLoginRef.current = true;
          }}
          trigger="custom routes explore cta tapped"
          upsellTrigger="custom routes explore cta tapped"
          hideUpsell
        />
      )}
    </>
  );
};

export default BuildCustomRouteButton;
